// Credits and usage (docs/cockpit/spec.md, ADR-0016, ADR-0017): this month's Credits, what used them by kind of
// task, Credit packs and auto-recharge. When the Credits run out, Runs pause at their next step until a top-up;
// people still read and work by hand. Credits never use safety amber. The Application API checks every action again.
import { useCallback, useEffect, useState } from "react";
import { ApiError, rupees, type Api, type CreditPack, type Credits as CreditsView } from "../api.js";
import { formatNumber, locale } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { CATALOGUES, useLanguage, useWords, type Words } from "../i18n/words.js";

const MILLI = 1000;
const number = (value: number, language: Language) => formatNumber(value, language, { maximumFractionDigits: 1 });
/** Milli-Credits as Credits for people, e.g. 1234500 → "1,234.5". */
export const creditsOf = (milli: number, language: Language = "en") => number(milli / MILLI, language);
const monthName = (month: string, language: Language) =>
  new Intl.DateTimeFormat(locale(language), { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${month}-01T00:00:00Z`),
  );
const packName = (pack: CreditPack, language: Language) =>
  CATALOGUES[language].creditsPack(number(pack.credits, language), rupees(pack.pricePaise));

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

function TopUp({
  packs,
  busy,
  onBuy,
}: {
  packs: readonly CreditPack[];
  busy: boolean;
  onBuy: (pack: CreditPack) => Promise<void>;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [chosen, setChosen] = useState<CreditPack | undefined>(undefined);
  if (chosen) {
    const [before, after] = w.creditsPayQuestion(number(chosen.credits, language));
    return (
      <div className="card" role="group" aria-label={w.creditsConfirmTopUp}>
        <p>
          {before}
          <strong>{rupees(chosen.pricePaise)}</strong>
          {after}
        </p>
        <button
          type="button"
          className="btn btn-go"
          disabled={busy}
          onClick={() => void onBuy(chosen).finally(() => setChosen(undefined))}
        >
          {w.creditsPay(rupees(chosen.pricePaise))}
        </button>{" "}
        <button type="button" className="btn" onClick={() => setChosen(undefined)}>
          {w.creditsCancel}
        </button>
      </div>
    );
  }
  return (
    <ul className="plain-list">
      {packs.map((p) => (
        <li key={p.id}>
          <button type="button" className="btn" disabled={busy} onClick={() => setChosen(p)}>
            {w.creditsBuy(packName(p, language))}
          </button>
        </li>
      ))}
    </ul>
  );
}

function AutoRecharge({
  credits,
  busy,
  onChange,
}: {
  credits: CreditsView;
  busy: boolean;
  onChange: (pack: string | null) => Promise<void>;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const on = credits.autoRecharge !== null;
  const pack = credits.autoRecharge ?? credits.packs[0]?.id ?? null;
  return (
    <div>
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          disabled={busy || pack === null}
          onChange={(e) => void onChange(e.target.checked ? pack : null)}
        />{" "}
        {w.creditsAutoRecharge}
      </label>
      {on ? (
        <label className="field">
          {w.creditsPackToBuy}{" "}
          <select value={credits.autoRecharge ?? ""} disabled={busy} onChange={(e) => void onChange(e.target.value)}>
            {credits.packs.map((p) => (
              <option key={p.id} value={p.id}>
                {packName(p, language)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

export function Credits({ api, newKey = () => crypto.randomUUID() }: { api: Api; newKey?: () => string }) {
  const w = useWords();
  const { language } = useLanguage();
  const [credits, setCredits] = useState<CreditsView | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.credits().then(setCredits, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api]);

  const attempt = useCallback(
    async (work: () => Promise<CreditsView>, done: string) => {
      setBusy(true);
      setNotice(undefined);
      setProblem(undefined);
      try {
        setCredits(await work());
        setNotice(done);
      } catch (error) {
        setProblem(problemOf(error, w));
      } finally {
        setBusy(false);
      }
    },
    [w],
  );

  if (!credits) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.creditsLoading}</p>;
  const used = Math.min(100, Math.max(0, credits.usedPercent));
  const left = w.creditsLeft(
    creditsOf(credits.budgetMilliCredits, language),
    creditsOf(credits.includedMilliCredits, language),
    credits.boughtMilliCredits > 0 ? creditsOf(credits.boughtMilliCredits, language) : undefined,
  );
  return (
    <>
      <p className="muted">{w.creditsIntro}</p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <section className="section" aria-labelledby="credits-month">
        <h2 id="credits-month">{monthName(credits.month, language)}</h2>
        <p>
          {left[0]}
          <strong>{creditsOf(credits.remainingMilliCredits, language)}</strong>
          {left[1]}
        </p>
        <meter
          min={0}
          max={100}
          value={used}
          aria-label={w.creditsUsedThisMonth}
          aria-valuetext={w.creditsUsedPercent(used)}
        />
        {credits.empty ? <p role="note">{w.creditsRunOut}</p> : null}
      </section>
      <section className="section" aria-labelledby="credits-by-kind">
        <h2 id="credits-by-kind">{w.creditsByKind}</h2>
        {credits.byKind.length === 0 ? (
          <p className="muted">{w.creditsNothingUsed}</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">{w.creditsKindOfTask}</th>
                  <th scope="col">{w.creditsModelTier}</th>
                  <th scope="col">{w.creditsTimes}</th>
                  <th scope="col">{w.creditsCredits}</th>
                </tr>
              </thead>
              <tbody>
                {credits.byKind.map((k) => (
                  <tr key={k.kind}>
                    <td>{k.label}</td>
                    <td>{k.tier === "T0" ? w.creditsNoModel : k.tier}</td>
                    <td>{number(k.count, language)}</td>
                    <td>{creditsOf(k.milliCredits, language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {credits.noCompletionToday.free + credits.noCompletionToday.charged > 0 ? (
        <section className="section" aria-labelledby="credits-no-answer">
          <h2 id="credits-no-answer">{w.creditsNoAnswerToday}</h2>
          <p>
            {w.creditsNoAnswerCounts(
              number(credits.noCompletionToday.free, language),
              number(credits.noCompletionToday.charged, language),
              creditsOf(credits.noCompletionToday.chargedMilliCredits, language),
              number(credits.noCompletionToday.freePerDay, language),
            )}
          </p>
        </section>
      ) : null}
      <section className="section" aria-labelledby="credits-top-up">
        <h2 id="credits-top-up">{w.creditsTopUp}</h2>
        <TopUp
          packs={credits.packs}
          busy={busy}
          onBuy={(pack) => attempt(() => api.topUp(pack.id, newKey()), w.creditsAdded(number(pack.credits, language)))}
        />
        <AutoRecharge
          credits={credits}
          busy={busy}
          onChange={(pack) =>
            attempt(() => api.setAutoRecharge(pack), pack === null ? w.creditsAutoRechargeOff : w.creditsAutoRechargeOn)
          }
        />
        {credits.topUps.length > 0 ? (
          <ul className="plain-list" aria-label={w.creditsTopUps}>
            {credits.topUps.map((t) => (
              <li key={`${t.at}-${t.pack}`}>
                {w.creditsPack(number(t.credits, language), rupees(t.amountPaise))}
                {t.automatic ? w.creditsAutomatic : ""}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </>
  );
}
